/* ── Lo score sheet: una pedina per partita, per arena E tornei ────────
   Erano cifre — 0 1 2 3 4 6 — e andavano imparate: il significato lo portava
   un codice, non la forma. Ora sono pedine, e si leggono da sinistra a destra
   come si legge: piena vinta, mezza patta, vuota persa. L'oro è la quarta, la
   vittoria che è valsa più di due punti (serie, boost o entrambi): è l'unica
   cosa della riga su cui si può decidere, e ridurla a "vinta" la butterebbe
   via. La quinta non è una pedina ed è dei soli tornei: il riposo.

   Il colore rinforza, non porta: chi non distingue verde e oro legge comunque
   pieno/mezzo/vuoto.

   Un foglio condiviso e non due copie (il precedente è `pedina-podio.css`):
   il taglio qui sotto è un intreccio di dichiarazioni più un misuratore, e
   due copie sarebbero due occasioni di sbagliarne una.
   ──────────────────────────────────────────────────────────────────── */

/*
 * IL TAGLIO. La fila porta TUTTE le partite — prima si fermava alle ultime
 * otto, un tetto di larghezza deciso in JS, dove la larghezza non si conosce
 * — e quando non ci stanno si perde la più VECCHIA, mai la più recente.
 *
 * Come: quando una riga flex eccede, l'allineamento si applica comunque, e con
 * `flex-end` l'eccedenza esce dal lato INIZIO (è il caso che l'allineamento
 * `safe` esiste per evitare). Quel lato è il sinistro, e l'area di scorrimento
 * non lo comprende: resta tagliato, senza barra e senza scorciatoie.
 *
 * ⚠️ Per la stessa ragione `scrollWidth > clientWidth` NON vede questo taglio:
 * `scrollWidth === clientWidth` anche con venti pedine fuori. Chi volesse
 * aggiungere un «+N» deve confrontare il BISOGNO calcolato con la larghezza
 * osservata, come fa `ScoreSheet.tsx`.
 *
 * `min-width: 0` è portante: un elemento flex ha per minimo automatico la sua
 * dimensione min-content — la fila intera — e senza questo la riga si allarga
 * invece di stringersi, con lo scorrimento orizzontale a 360px.
 *
 * Questo è lo stato di RIPIEGO, cioè quello che si vede se il misuratore non
 * parla (jsdom, nessun `ResizeObserver`, il primo fotogramma): un guasto costa
 * l'allineamento, non la pedina più recente.
 */
.score-sheet {
  display: flex;
  flex-wrap: nowrap;
  align-items: center;
  gap: 4px;
  min-width: 0;
  overflow: hidden;
  justify-content: flex-end;
  /* La sfumatura dice «ce n'è altre, più vecchie» dove la fila è tagliata.
     Una rampa di alpha e non un velo colorato: `transparent → #000` è identica
     nei due temi e non chiede nessun token. La rampa è larga una pedina più il
     gap (14px) perché la mezzaluna tagliata si DISSOLVA: una «vinta» tagliata a
     metà è una «patta» specchiata, e qui la maschera non è un ornamento, è la
     cura. Il prefisso `-webkit-` come in `sweep-ring.css`. */
  -webkit-mask-image: linear-gradient(to right, transparent 0, #000 14px);
  mask-image: linear-gradient(to right, transparent 0, #000 14px);
}

/*
 * Ci stanno tutte: lo dice il misuratore, perché il CSS non sa distinguere i
 * due casi. Allora la fila torna attaccata a sinistra — dove sta quando non
 * c'è niente da tagliare — e la sfumatura si spegne, che altrimenti sfumerebbe
 * la più vecchia senza che nulla sia andato perduto.
 */
.score-sheet[data-sta-tutta] {
  justify-content: flex-start;
  -webkit-mask-image: none;
  mask-image: none;
}

.score-disc {
  width: 12px;
  height: 12px;
  border-radius: 50%;
  flex: none;
}

.score-disc[data-kind="win"] {
  background: var(--color-forest-dark);
  box-shadow: inset 0 0 0 1px var(--color-primary-glow-strong);
}

/* Ornamento saturo (medaglie, aloni, pedine): gli esadecimali stanno qui
   perché l'oro è una superficie disegnata, non una tinta del tema. */
.score-disc[data-kind="winPlus"] {
  background: radial-gradient(circle at 34% 28%, #FFE29A, #E0A32B 65%, #B87F12);
  box-shadow: inset 0 0 0 1px rgba(120, 82, 10, 0.5);
}

/* mezza: la metà piena dice "un punto", il bordo tiene la pedina intera */
.score-disc[data-kind="draw"] {
  background: linear-gradient(90deg, var(--color-brown-medium) 50%, transparent 50%);
  box-shadow: inset 0 0 0 1.2px var(--color-brown-medium);
}

.score-disc[data-kind="loss"] {
  background: transparent;
  box-shadow: inset 0 0 0 1.2px var(--color-shadow-deeper);
}

/*
 * Il riposo, e solo nei tornei a turni: l'unica che non è una pedina.
 *
 * Pieno, mezzo e vuoto dicono i PUNTI, e un cerchio vuoto è già «persa»: la
 * differenza qui non può essere la campitura ma la SAGOMA — la casella in cui
 * una pedina non c'è stata. Si legge anche in bianco e nero, che è il motivo
 * per cui le altre quattro sono forme e non colori.
 */
.score-disc[data-kind="bye"] {
  border-radius: 3px;
  background: transparent;
  box-shadow: inset 0 0 0 1.2px var(--color-text-secondary);
}
